import { filterGenres, filterRegions, filterYears, sortOptions } from "@/mocks/movies";

export interface FilterState {
  regions: string[];
  years: string[];
  genres: string[];
  sort: string;
}

interface FilterPanelProps {
  state: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

function ChipGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const rest = options.filter((o) => o !== "全部");
  return (
    <div className="border-b border-background-200/70 py-4 first:pt-0 last:border-b-0">
      <h3 className="mb-3 text-sm font-semibold text-foreground-800">{title}</h3>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => selected.length > 0 && selected.forEach(onToggle)}
          className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
            selected.length === 0
              ? "bg-accent-500 text-background-50"
              : "bg-background-200 text-foreground-600 hover:bg-background-300"
          }`}
        >
          全部
        </button>
        {rest.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                active
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-200 text-foreground-600 hover:bg-background-300"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({ state, onChange, onReset }: FilterPanelProps) {
  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-filter-3-line text-accent-500" aria-hidden="true"></i>
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-accent-500"
        >
          重置
        </button>
      </div>

      <ChipGroup
        title="地区"
        options={filterRegions}
        selected={state.regions}
        onToggle={(value) => onChange({ ...state, regions: toggle(state.regions, value) })}
      />
      <ChipGroup
        title="年份"
        options={filterYears}
        selected={state.years}
        onToggle={(value) => onChange({ ...state, years: toggle(state.years, value) })}
      />
      <ChipGroup
        title="类型"
        options={filterGenres}
        selected={state.genres}
        onToggle={(value) => onChange({ ...state, genres: toggle(state.genres, value) })}
      />

      <div className="py-4 pt-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground-800">排序</h3>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((option) => {
            const active = state.sort === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...state, sort: option.value })}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200 text-foreground-600 hover:bg-background-300"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}